import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Stack, Typography } from '@mui/material';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { useAuth } from '../AuthContext';
import type { BrokerDoc } from './BrokerDrawer';

type Submission = {id: string; status: string; profile: any; submittedAt: any};
type TeamMember = {id: string; name: string; phone10: string; role: string; status: string};
type Visit = {id: string; status: string; dayKey: string; metByName: string;
  assessment?: {potential?: string; projectStatus?: string; agencyRole?: string}};
export default function BrokerProfileReviewDialog({broker, onClose}: {
  broker: BrokerDoc | null; onClose: () => void;
}) {
  const {roleId} = useAuth();
  const canReview = ['admin', 'super-admin'].includes(String(roleId));
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [visits, setVisits] = useState<Visit[]>([]);
  const [projectNames, setProjectNames] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [inviteMessage, setInviteMessage] = useState('');
  const [manualUrl, setManualUrl] = useState('');
  useEffect(() => {
    if (!broker) return;
    const off1 = onSnapshot(query(collection(db, 'brokerProfileSubmissions'),
      where('brokerId', '==', broker.id)), (snap) => setSubmissions(snap.docs
      .map((d) => ({id: d.id, ...(d.data() as any)}))
      .sort((a, b) => (b.submittedAt?.toMillis?.() || 0) -
        (a.submittedAt?.toMillis?.() || 0))), (err) => setError(err.message));
    const off2 = onSnapshot(collection(db, 'brokers', broker.id, 'teamMembers'),
      (snap) => setMembers(snap.docs.map((d) => ({id: d.id, ...(d.data() as any)}))),
      (err) => setError(err.message));
    const off3 = onSnapshot(query(collection(db, 'brokerVisits'),
      where('brokerId', '==', broker.id)), (snap) => setVisits(snap.docs
      .map((d) => ({id: d.id, ...(d.data() as any)}))
      .sort((a, b) => b.dayKey.localeCompare(a.dayKey))),
    (err) => setError(err.message));
    return () => {off1(); off2(); off3();};
  }, [broker]);
  useEffect(() => onSnapshot(collection(db, 'projects'), (snap) => {
    const names: Record<string, string> = {};
    snap.docs.forEach((d) => {names[d.id] = String(d.data().name || d.id);});
    setProjectNames(names);
  }), []);
  const review = async (id: string, decision: 'approve' | 'reject') => {
    setBusy(true); setError('');
    try {
      await httpsCallable(functions, 'brokerProfileReview')({submissionId: id, decision});
    } catch (err: any) { setError(err?.message || 'Could not review submission.'); }
    finally { setBusy(false); }
  };
  const reviewMember = async (id: string, decision: 'verify' | 'reject') => {
    if (!broker) return;
    setBusy(true); setError('');
    try {
      await httpsCallable(functions, 'brokerTeamMemberReview')({
        brokerId: broker.id, memberId: id, decision,
      });
    } catch (err: any) { setError(err?.message || 'Could not review team member.'); }
    finally { setBusy(false); }
  };
  const sendProfileForm = async () => {
    if (!broker) return;
    setBusy(true); setError(''); setInviteMessage(''); setManualUrl('');
    try {
      const result: any = await httpsCallable(functions, 'brokerProfileInvite')({brokerId: broker.id});
      if (result.data?.sent) setInviteMessage('Profile link sent on WhatsApp.');
      else {
        setInviteMessage('Automatic WhatsApp delivery was unavailable. Send the prepared message manually.');
        setManualUrl(String(result.data?.manualUrl || ''));
      }
    } catch (err: any) { setError(err?.message || 'Could not create the profile link.'); }
    finally { setBusy(false); }
  };
  return <Dialog open={!!broker} onClose={onClose} fullWidth maxWidth="sm">
    <DialogTitle>{broker?.brokerName || 'Broker'} · profile and visits</DialogTitle>
    <DialogContent><Stack spacing={2} sx={{pt: 1}}>
      {error && <Alert severity="error">{error}</Alert>}
      {inviteMessage && <Alert severity="info">{inviteMessage}
        {manualUrl && <Button href={manualUrl} target="_blank" rel="noopener noreferrer">
          Open WhatsApp
        </Button>}
      </Alert>}
      {canReview && <Button disabled={busy} variant="outlined" onClick={sendProfileForm}>
        Send profile form
      </Button>}
      <Box><Typography fontWeight={700}>Current profile</Typography>
        <Typography variant="body2">{broker?.phoneE164 || '—'} · {broker?.agencyName || 'Independent'}</Typography>
        <Typography variant="body2">Email: {broker?.email || '—'} · DOB: {broker?.dateOfBirth || '—'}</Typography>
        <Typography variant="body2">Team size: {broker?.teamSize ?? '—'} · Experience: {broker?.yearsExperience ?? '—'} years</Typography>
        <Typography variant="body2">Potential: {broker?.brokerPotential || 'Unknown'} · Agency relationship: {broker?.agencyRoleAssessment || 'Unknown'}</Typography>
        {broker?.brokerInterestedProjectIds?.length ? <Typography variant="body2">
          Interested in: {broker.brokerInterestedProjectIds.map((id) => projectNames[id] || id).join(', ')}
        </Typography> : null}
        {(broker as any)?.claimedAgencyName && <Alert severity="info" sx={{mt: 1}}>
          Agency claimed in form: {(broker as any).claimedAgencyName}. Verify it before changing the agency record.
        </Alert>}
      </Box>
      <Box><Typography fontWeight={700}>Broker-submitted details</Typography>
        {submissions.length === 0 && <Typography variant="body2" color="text.secondary">No submissions yet.</Typography>}
        {submissions.map((s) => <Box key={s.id} sx={{border: '1px solid #e8e3da', borderRadius: 2, p: 1.5, mt: 1}}>
          <Stack direction="row" gap={1} alignItems="center">
            <Typography fontWeight={700}>{s.profile?.name || 'Profile'}</Typography>
            <Chip size="small" label={s.status} />
          </Stack>
          <Typography variant="body2">Email: {s.profile?.email || '—'} · DOB: {s.profile?.dateOfBirth || '—'}</Typography>
          <Typography variant="body2">Agency: {s.profile?.agencyName || '—'} · City: {s.profile?.city || '—'} · Area: {s.profile?.area || '—'}</Typography>
          <Typography variant="body2">RERA: {s.profile?.reraNumber || '—'} · Team size: {s.profile?.teamSize ?? '—'} · Experience: {s.profile?.yearsExperience ?? '—'} years</Typography>
          <Typography variant="body2">Team members: {s.profile?.members?.length || 0}</Typography>
          {s.profile?.interestedProjectIds?.length ? <Typography variant="body2">
            Interested in: {s.profile.interestedProjectIds.map((id: string) => projectNames[id] || id).join(', ')}
          </Typography> : null}
          {s.profile?.members?.map((m: any, i: number) => <Typography key={i} variant="body2">
            {m.name} · {m.phone10} · {m.role || 'Role not given'}
          </Typography>)}
          {canReview && s.status === 'pending' && <Stack direction="row" gap={1} sx={{mt: 1}}>
            <Button disabled={busy} variant="contained" onClick={() => review(s.id, 'approve')}>Approve details</Button>
            <Button disabled={busy} onClick={() => review(s.id, 'reject')}>Reject</Button>
          </Stack>}
        </Box>)}
      </Box>
      <Box><Typography fontWeight={700}>Team members awaiting verification</Typography>
        {members.length === 0 && <Typography variant="body2" color="text.secondary">None added.</Typography>}
        {members.map((m) => <Box key={m.id} sx={{py: 0.5}}>
          <Typography variant="body2">{m.name} · {m.phone10} · {m.role || 'Member'} · {m.status}</Typography>
          {canReview && m.status === 'unverified' && <Stack direction="row" gap={1}>
            <Button size="small" disabled={busy} onClick={() => reviewMember(m.id, 'verify')}>Verify</Button>
            <Button size="small" disabled={busy} onClick={() => reviewMember(m.id, 'reject')}>Reject</Button>
          </Stack>}
        </Box>)}
      </Box>
      <Box><Typography fontWeight={700}>Visit history</Typography>
        {visits.length === 0 && <Typography variant="body2" color="text.secondary">No broker-only visits yet.</Typography>}
        {visits.map((v) => <Typography key={v.id} variant="body2">
          {v.dayKey} · {v.status} · {v.metByName || 'No meeting'}
          {v.assessment?.potential ? ` · ${v.assessment.potential} potential` : ''}
        </Typography>)}
      </Box>
    </Stack></DialogContent>
    <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
  </Dialog>;
}
